import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import { calibrationKeys, fleetMeasurementKeys, scheduleKeys } from "../query-keys";
import type { BuildId, CalibrationId, CalibrationState } from "@/types/identifiers";
import type { components } from "../generated/schema";

export { calibrationKeys } from "../query-keys";

export function useCalibrationHistory(buildId: BuildId) {
  return useQuery({
    queryKey: calibrationKeys.history(buildId),
    queryFn: async () => {
      const result = await api.GET(
        "/api/builds/{build_id}/calibrations/history",
        { params: { path: { build_id: buildId } } },
      );
      return throwIfError(result, "Failed to fetch calibration history");
    },
    enabled: !!buildId,
  });
}

export function useCalibrations(buildId: BuildId) {
  return useQuery({
    queryKey: calibrationKeys.byBuild(buildId),
    queryFn: async () => {
      const result = await api.GET(
        "/api/builds/{build_id}/calibrations",
        { params: { path: { build_id: buildId } } },
      );
      return throwIfError(result, "Failed to fetch calibrations");
    },
    enabled: !!buildId,
  });
}

/** What the boat holds right now. Read on demand: every read is 21 NVP round
 * trips, so callers enable it only while the boat is online. */
export function useLiveCalibration(buildId: BuildId, enabled: boolean) {
  return useQuery({
    queryKey: calibrationKeys.live(buildId),
    queryFn: async () => {
      const result = await api.GET(
        "/api/builds/{build_id}/calibrations/live",
        { params: { path: { build_id: buildId } } },
      );
      return throwIfError(result, "Failed to read the boat's calibration");
    },
    enabled: !!buildId && enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Write the named params of a golden to the boat. Changes hardware, not the
 * database, so only the live reading goes stale. */
export function useWriteCalibrationToBoat(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      calibrationId,
      params,
    }: {
      calibrationId: string;
      params: components["schemas"]["NvpCalParameter"][];
    }) => {
      const result = await api.POST(
        "/api/builds/{build_id}/calibrations/write-to-boat",
        {
          params: { path: { build_id: buildId } },
          body: { calibration_id: calibrationId, params },
        },
      );
      return throwIfError(result, "Failed to write the calibration to the boat");
    },
    onSettled: async () => {
      // A read still in flight when the write lands would come back as the
      // fresh post-write state: TanStack reuses a pending fetch while the
      // cache is empty. Cancel it, then read the boat again.
      await qc.cancelQueries({ queryKey: calibrationKeys.live(buildId) });
      await qc.invalidateQueries({ queryKey: calibrationKeys.live(buildId) });
    },
  });
}

export function useFetchCalibration(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fetchedBy: string) => {
      const result = await api.POST(
        "/api/builds/{build_id}/calibrations/fetch",
        {
          params: { path: { build_id: buildId } },
          body: { fetched_by: fetchedBy },
        },
      );
      return throwIfError(result, "Failed to fetch calibration from NVP");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calibrationKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: fleetMeasurementKeys.all });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useToggleHidden(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (calId: CalibrationId) => {
      const result = await api.POST(
        "/api/builds/{build_id}/calibrations/{cal_id}/toggle-hidden",
        {
          params: { path: { build_id: buildId, cal_id: calId } },
        },
      );
      return throwIfError(result, "Failed to toggle hidden");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calibrationKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: fleetMeasurementKeys.all });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useSignOffCalibration(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      calId,
      signedBy,
    }: {
      calId: CalibrationId;
      signedBy: string;
    }) => {
      const result = await api.POST(
        "/api/builds/{build_id}/calibrations/{cal_id}/sign-off",
        {
          params: { path: { build_id: buildId, cal_id: calId } },
          body: { signed_by: signedBy },
        },
      );
      return throwIfError(result, "Failed to sign off calibration");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calibrationKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: fleetMeasurementKeys.all });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useSetCalibrationState(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      calId,
      state,
    }: {
      calId: CalibrationId;
      state: CalibrationState;
    }) => {
      const result = await api.POST(
        "/api/builds/{build_id}/calibrations/{cal_id}/state",
        {
          params: { path: { build_id: buildId, cal_id: calId } },
          body: { state },
        },
      );
      return throwIfError(result, "Failed to set calibration state");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calibrationKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: calibrationKeys.history(buildId) });
    },
  });
}

export function useUnlockCalibration(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (calId: CalibrationId) => {
      const result = await api.POST(
        "/api/builds/{build_id}/calibrations/{cal_id}/unlock",
        {
          params: { path: { build_id: buildId, cal_id: calId } },
        },
      );
      return throwIfError(result, "Failed to unlock calibration");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calibrationKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: fleetMeasurementKeys.all });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}
